import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import {
  ArrowLeft, Search, Download, Heart, Globe, Users, Calendar,
  Mail, MessageSquare, ChevronDown, ChevronUp, Send, X, Eye, Sparkles,
} from "lucide-react";

const GOLD = "#D4AF37";
const TEAL = "#0F4F4F";

const StatBadge = ({ icon: Icon, label, value, color = GOLD }) => (
  <div className="glass rounded-2xl p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-widest text-white/45">{label}</span>
      <Icon size={16} style={{ color }} />
    </div>
    <div className="serif text-3xl font-light" style={{ color: "rgba(255,255,255,0.92)" }}>
      {value}
    </div>
  </div>
);

const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `vor ${s}s`;
  if (s < 3600) return `vor ${Math.floor(s / 60)}m`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)}h`;
  if (s < 30 * 86400) return `vor ${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" });
};

const toCSV = (rows) => {
  if (!rows.length) return "";
  const headers = ["created_at", "name", "email", "country", "motivation", "source", "user_id"];
  const esc = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const head = headers.join(",");
  const body = rows.map((r) => headers.map((h) => esc(r[h])).join(",")).join("\n");
  return `${head}\n${body}`;
};

const download = (name, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

/* Broadcast Modal — subject + body_html + personalization + preview + send */
function BroadcastModal({ open, onClose, total }) {
  const [subject, setSubject] = useState("Ein Herzimpuls für {{first_name}}");
  const [body, setBody] = useState(
    "<p>Danke, dass du Teil der Herzbewegung bist.</p>\n<p>Diese Woche ein Impuls: <strong>Höre einem Menschen wirklich zu — ohne zu antworten.</strong></p>\n<p>Von Herzen,<br/>Nenad</p>"
  );
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState(null);

  React.useEffect(() => {
    if (!open) {
      setPreview(null); setResult(null); setConfirm(false); setErr(null); setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const doPreview = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/admin/herz/herzbotschafter/broadcast", {
        subject, body_html: body, dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/admin/herz/herzbotschafter/broadcast", {
        subject, body_html: body, dry_run: false,
      });
      setResult(data); setConfirm(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(8,19,40,0.85)" }}
      data-testid="broadcast-modal"
    >
      <div
        className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border"
        style={{ background: "#0F1A30", borderColor: `${GOLD}44` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-bold" style={{ color: GOLD }}>
              HERZ-BROADCAST
            </div>
            <h2 className="serif text-2xl font-light mt-1 text-white">
              Alle Herzbotschafter anschreiben
            </h2>
            <p className="text-xs mt-1 text-white/55">
              {total} Empfänger:innen erhalten diesen Impuls per E-Mail.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10"
            style={{ color: "rgba(255,255,255,0.7)" }}
            data-testid="broadcast-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {result ? (
          /* SUCCESS STATE */
          <div className="p-8 text-center" data-testid="broadcast-result">
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: TEAL }}
            >
              <Send size={28} style={{ color: GOLD }} />
            </div>
            <h3 className="serif text-2xl text-white font-light">Broadcast gesendet.</h3>
            <p className="mt-2 text-white/70 text-sm">
              <span className="font-bold" style={{ color: GOLD }}>{result.sent}</span> von {result.total} E-Mails erfolgreich verschickt.
              {result.failed > 0 && (
                <> · <span style={{ color: "#E88B8B" }}>{result.failed} Fehler</span></>
              )}
            </p>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold"
              style={{ background: GOLD, color: "#0A3838" }}
              data-testid="broadcast-result-close"
            >
              Fertig
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Personalization hint */}
            <div
              className="rounded-lg p-3 text-[11px] flex items-start gap-2"
              style={{ background: "rgba(212,175,55,0.08)", border: `1px solid ${GOLD}33`, color: GOLD }}
            >
              <Sparkles size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-bold tracking-wider uppercase mb-1">Personalisierungs-Tokens</div>
                <div className="text-white/70">
                  <code className="text-white">{"{{first_name}}"}</code> · <code className="text-white">{"{{name}}"}</code> · <code className="text-white">{"{{country}}"}</code> · <code className="text-white">{"{{email}}"}</code>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5 text-white/60">
                Betreff
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border bg-white/5 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                data-testid="broadcast-subject"
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase font-bold mb-1.5 text-white/60">
                Nachricht (HTML erlaubt: &lt;p&gt;, &lt;strong&gt;, &lt;br/&gt;, &lt;a href&gt;)
              </label>
              <textarea
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border bg-white/5 text-white text-sm font-mono focus:outline-none focus:border-[#D4AF37]/50 resize-none"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                data-testid="broadcast-body"
              />
            </div>

            {/* Preview */}
            {preview && (
              <div
                className="rounded-lg overflow-hidden border"
                style={{ borderColor: `${GOLD}44` }}
                data-testid="broadcast-preview"
              >
                <div
                  className="px-4 py-2 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-bold"
                  style={{ background: TEAL, color: GOLD }}
                >
                  <Eye size={12} /> Vorschau · Erste:r Empfänger:in ({preview.preview_recipient?.name})
                </div>
                <div className="p-2 bg-white">
                  <div className="text-xs px-2 py-1.5 mb-2 border-b border-black/10 text-black/70">
                    <strong>Betreff:</strong> {preview.preview_subject}
                  </div>
                  <div
                    className="text-[13px]"
                    dangerouslySetInnerHTML={{ __html: preview.preview_html }}
                  />
                </div>
              </div>
            )}

            {err && (
              <div
                className="rounded-lg px-4 py-3 text-sm border"
                style={{ background: "rgba(232,139,139,0.1)", borderColor: "#E88B8B55", color: "#E88B8B" }}
                data-testid="broadcast-error"
              >
                {err}
              </div>
            )}

            {/* Actions */}
            {confirm ? (
              <div
                className="rounded-lg p-4 border"
                style={{ background: "rgba(212,175,55,0.08)", borderColor: `${GOLD}55` }}
                data-testid="broadcast-confirm"
              >
                <p className="text-sm text-white mb-3">
                  Bereit, den Impuls an <strong style={{ color: GOLD }}>{total} Herzbotschafter:innen</strong> zu senden?
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={doSend}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
                    style={{ background: `linear-gradient(90deg, ${GOLD}, ${TEAL})`, color: "#0A3838" }}
                    data-testid="broadcast-send-confirm"
                  >
                    <Send size={14} /> {busy ? "Sende ..." : `Ja, jetzt senden`}
                  </button>
                  <button
                    onClick={() => setConfirm(false)}
                    disabled={busy}
                    className="px-5 py-2.5 rounded-full border text-sm text-white/70"
                    style={{ borderColor: "rgba(255,255,255,0.15)" }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={doPreview}
                  disabled={busy || !subject || !body}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm disabled:opacity-40"
                  style={{ borderColor: `${GOLD}55`, color: GOLD }}
                  data-testid="broadcast-preview-btn"
                >
                  <Eye size={14} /> {busy && !preview ? "Lade Preview ..." : "Vorschau"}
                </button>
                <button
                  onClick={() => setConfirm(true)}
                  disabled={!subject || !body}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40"
                  style={{ background: GOLD, color: "#0A3838" }}
                  data-testid="broadcast-send-btn"
                >
                  <Send size={14} /> An {total} senden
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function HerzbotschafterAdmin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(null);
  const [loadingRows, setLoadingRows] = useState(true);
  const [error, setError] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/herz/herzbotschafter`);
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Fehler beim Laden");
      } finally {
        setLoadingRows(false);
      }
    })();
  }, [user, loading, nav]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = rows;
    if (term) {
      out = out.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(term) ||
          (r.email || "").toLowerCase().includes(term) ||
          (r.country || "").toLowerCase().includes(term) ||
          (r.motivation || "").toLowerCase().includes(term)
      );
    }
    out = [...out].sort((a, b) => {
      const av = (a[sortBy] || "");
      const bv = (b[sortBy] || "");
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, q, sortBy, sortDir]);

  const stats = useMemo(() => {
    const countries = new Set(rows.map((r) => r.country).filter(Boolean));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newToday = rows.filter((r) => new Date(r.created_at || 0) >= today).length;
    const last7 = rows.filter((r) => {
      const d = new Date(r.created_at || 0);
      return Date.now() - d.getTime() < 7 * 86400 * 1000;
    }).length;
    return { total: rows.length, countries: countries.size, newToday, last7 };
  }, [rows]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("desc"); }
  };

  const exportCsv = () => {
    const name = `herzbotschafter-${new Date().toISOString().slice(0, 10)}.csv`;
    download(name, toCSV(filtered));
  };

  const sortIcon = (k) =>
    sortBy !== k ? null : sortDir === "asc" ? <ChevronUp size={12} className="inline ml-1" /> : <ChevronDown size={12} className="inline ml-1" />;

  if (loading || !user) return <div className="pt-32 text-center text-white/60">Lade…</div>;

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="herzbotschafter-admin">
      <div className="container-sat">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase mb-3 transition hover:translate-x-[-2px]"
              style={{ color: GOLD }}
              data-testid="herzbotschafter-admin-back"
            >
              <ArrowLeft size={12} /> Zurück zum Admin
            </Link>
            <div className="overline">HERZBEWEGUNG · ADMIN</div>
            <h1 className="headline-xl text-4xl mt-2 flex items-center gap-3">
              <Heart size={28} style={{ color: GOLD, fill: GOLD }} />
              Herzbotschafter:innen
            </h1>
            <p className="serif italic mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>
              Alle Menschen, die Ja zur Herzbewegung gesagt haben.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBroadcast(true)}
              disabled={!rows.length}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-40"
              style={{ background: `linear-gradient(90deg, ${GOLD}, ${TEAL})`, color: "#0A3838" }}
              data-testid="herzbotschafter-admin-broadcast-open"
            >
              <Send size={14} /> Alle anschreiben
            </button>
            <button
              onClick={exportCsv}
              disabled={!filtered.length}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm transition disabled:opacity-40"
              style={{ borderColor: `${GOLD}55`, color: GOLD }}
              data-testid="herzbotschafter-admin-export"
            >
              <Download size={14} /> CSV Export ({filtered.length})
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBadge icon={Users} label="Gesamt" value={stats.total} />
          <StatBadge icon={Calendar} label="Heute neu" value={stats.newToday} color="#5A9B7E" />
          <StatBadge icon={Calendar} label="Letzte 7 Tage" value={stats.last7} color={TEAL} />
          <StatBadge icon={Globe} label="Länder" value={stats.countries} color="#7C4FBA" />
        </div>

        {/* Search */}
        <div className="mb-4 relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Name, E-Mail, Land oder Motivation …"
            className="w-full pl-9 pr-4 py-2.5 rounded-full glass border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/50"
            data-testid="herzbotschafter-admin-search"
          />
        </div>

        {/* Table */}
        <div className="glass rounded-2xl overflow-hidden">
          {loadingRows ? (
            <div className="p-8 text-white/55 text-sm">Lade …</div>
          ) : error ? (
            <div className="p-8 text-red-400/80 text-sm" data-testid="herzbotschafter-admin-error">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/45">
                  <tr>
                    <th className="text-left px-5 py-4 cursor-pointer" onClick={() => toggleSort("created_at")}>
                      Wann {sortIcon("created_at")}
                    </th>
                    <th className="text-left px-5 py-4 cursor-pointer" onClick={() => toggleSort("name")}>
                      Name {sortIcon("name")}
                    </th>
                    <th className="text-left px-5 py-4 cursor-pointer" onClick={() => toggleSort("email")}>
                      E-Mail {sortIcon("email")}
                    </th>
                    <th className="text-left px-5 py-4 cursor-pointer" onClick={() => toggleSort("country")}>
                      Land {sortIcon("country")}
                    </th>
                    <th className="text-left px-5 py-4">Motivation</th>
                    <th className="text-left px-5 py-4">Quelle</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                        Noch keine Herzbotschafter:innen.
                      </td>
                    </tr>
                  )}
                  {filtered.map((r) => {
                    const isOpen = expanded === r.id;
                    const short = (r.motivation || "").slice(0, 100);
                    return (
                      <React.Fragment key={r.id}>
                        <tr
                          className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer transition"
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          data-testid={`herzbotschafter-row-${r.id}`}
                        >
                          <td className="px-5 py-3 text-white/70 whitespace-nowrap">{timeAgo(r.created_at)}</td>
                          <td className="px-5 py-3 text-white/90 font-medium">{r.name || "—"}</td>
                          <td className="px-5 py-3 text-white/70">
                            <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#D4AF37] inline-flex items-center gap-1.5">
                              <Mail size={11} /> {r.email}
                            </a>
                          </td>
                          <td className="px-5 py-3 text-white/70">{r.country || "—"}</td>
                          <td className="px-5 py-3 text-white/75 max-w-[380px] truncate" title={r.motivation}>
                            {short}{(r.motivation || "").length > 100 ? "…" : ""}
                          </td>
                          <td className="px-5 py-3 text-white/50 text-xs uppercase tracking-wider">{r.source || "—"}</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t border-white/5 bg-white/[0.03]">
                            <td colSpan={6} className="px-8 py-5">
                              <div className="flex items-start gap-3">
                                <MessageSquare size={16} style={{ color: GOLD }} className="mt-1 shrink-0" />
                                <div>
                                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: GOLD }}>
                                    Was bewegt {r.name?.split(" ")[0] || "diese Person"}
                                  </div>
                                  <p className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap max-w-3xl">
                                    {r.motivation}
                                  </p>
                                  <div className="mt-3 flex items-center gap-4 text-[11px] text-white/45">
                                    <span>ID: {r.id?.slice(0, 8)}…</span>
                                    <span>Registriert: {new Date(r.created_at).toLocaleString("de-CH")}</span>
                                    {r.user_id && <span>User-Account: verknüpft</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-white/35">
          <Link to="/admin" className="hover:text-white">← Zurück zum Admin-Dashboard</Link>
        </div>
      </div>
      <BroadcastModal open={showBroadcast} onClose={() => setShowBroadcast(false)} total={rows.length} />
    </div>
  );
}
